import React, { FC } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { CustomButton } from '../../../components/buttons/customButton';
import DigtectiveLogoSmall from '../../../assets/images/logos/digtective-colorful-small.svg';
import { useUpdateOrganizationSettings } from '../../../apiHooks/mutations/organizationMutations/organizationSettingsMutations';
import { useUpdateOnboardingStep } from '../../../apiHooks/mutations/onboarding/useUpdateOnboardingStep';
import { useOrganizationSettingsStore } from '../../../store/organizationSettingsStore';
import CurrencySelect from '../../../components/dropdownSelects/currencySelect';

interface DomainStepProps {
  onContinue: () => void;
}

const WHY_ITEMS = [
  { key: 'setup.general.why1' },
  { key: 'setup.general.why2' },
  { key: 'setup.general.why3' },
];

const DomainStep: FC<DomainStepProps> = ({ onContinue }) => {
  const { t } = useTranslation();
  const organizationSettings = useOrganizationSettingsStore((s) => s.organizationSettings);
  const { mutateAsync: updateSettings, isLoading: isSavingSettings } = useUpdateOrganizationSettings();
  const { mutateAsync: updateStep, isLoading: isSavingStep } = useUpdateOnboardingStep();

  const inputIsRequiredMsg = String(t('inputIsRequired'));

  const formik = useFormik({
    initialValues: {
      website: organizationSettings.website ?? '',
      currency: organizationSettings.currency ?? '',
    },
    validationSchema: Yup.object({
      website: Yup.string().required(inputIsRequiredMsg),
      currency: Yup.string().required(inputIsRequiredMsg),
    }),
    onSubmit: async (values) => {
      await updateSettings({ website: values.website, currency: values.currency });
      await updateStep({ step: 'domain', status: 'completed' });
      onContinue();
    },
  });

  const isLoading = isSavingSettings || isSavingStep;

  return (
    <div className="flex w-full max-w-3xl gap-6">
      {/* Left — form */}
      <div className="flex flex-1 flex-col rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
        <div className="mb-5 flex items-center gap-4">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10">
            <img src={DigtectiveLogoSmall} alt="Digtective" className="h-7 w-7 object-contain" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{t('setup.general.title')}</h1>
            <p className="text-sm text-gray-400">{t('setup.general.subtitle')}</p>
          </div>
        </div>

        <div className="mb-6 h-px w-full bg-gray-100" />

        <form onSubmit={formik.handleSubmit} className="flex flex-col gap-5">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700">
              {t('setup.general.websiteLabel')}
            </label>
            <div className="flex items-center overflow-hidden rounded-lg border border-gray-200 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/10">
              <span className="select-none border-r border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-400">
                https://
              </span>
              <input
                name="website"
                type="text"
                value={formik.values.website}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={String(t('setup.general.websitePlaceholder'))}
                className="flex-1 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 outline-none"
              />
            </div>
            {formik.touched.website && formik.errors.website && (
              <p className="mt-1 text-xs text-red">{formik.errors.website}</p>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700">
              {t('currency')}
            </label>
            <CurrencySelect formik={formik} fullWidth size="small" />
            {formik.touched.currency && formik.errors.currency && (
              <p className="mt-1 text-xs text-red">{formik.errors.currency}</p>
            )}
          </div>

          <CustomButton
            type="submit"
            className="mt-2 w-full justify-center py-2.5"
            disabled={isLoading}
          >
            {isLoading ? t('setup.saving') : `${t('setup.continue')} →`}
          </CustomButton>
        </form>
      </div>

      {/* Right — why this matters */}
      <div className="flex w-64 flex-shrink-0 flex-col rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t('setup.general.whyTitle')}
        </p>
        <div className="flex flex-col gap-4">
          {WHY_ITEMS.map((item) => (
            <div key={item.key} className="flex gap-3">
              <div className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary/40" />
              <p className="text-sm leading-relaxed text-gray-500">{t(item.key)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DomainStep;
